import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { formatProposalMoneySafe } from "@/lib/proposalTheme";
import { isReasonablyValidEmail } from "@/lib/proposalCustomerActions";
import type { ProposalCustomerPricingDisplay } from "@shared/proposalCustomerPricing";
import {
  DEFAULT_APPROVAL_WHATSAPP_COUNTRY,
  listApprovalWhatsAppCountries,
  maskWhatsAppDisplay,
} from "@shared/proposalApprovalOtp";
import { Loader2 } from "lucide-react";
import { useMemo } from "react";

export type ProposalApprovalConfirmDialogVariant = "proposal" | "variation_order";

export type VariationOrderApprovalSummary = {
  voNumber: string;
  proposalRef?: string | null;
  amountDueUponConfirmation: number;
  revisedContractValue: number;
  variationAmount: number;
};

export type ProposalOtpApprovalStep = "details" | "otp";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  variant?: ProposalApprovalConfirmDialogVariant;
  proposalRef: string;
  optionLabel?: string;
  customerPricing?: ProposalCustomerPricingDisplay | null;
  hidePricingSummary?: boolean;
  variationOrderSummary?: VariationOrderApprovalSummary | null;
  /** Legacy VO email-only path */
  approvalEmail: string;
  onApprovalEmailChange: (value: string) => void;
  onConfirm: () => void;
  confirming?: boolean;
  confirmDisabled?: boolean;
  /** Proposal WhatsApp OTP flow */
  otpStep?: ProposalOtpApprovalStep;
  approverName?: string;
  onApproverNameChange?: (value: string) => void;
  whatsappNumber?: string;
  onWhatsappNumberChange?: (value: string) => void;
  whatsappCountry?: string;
  onWhatsappCountryChange?: (value: string) => void;
  otpCode?: string;
  onOtpCodeChange?: (value: string) => void;
  maskedWhatsApp?: string | null;
  sendingOtp?: boolean;
  verifyingOtp?: boolean;
  onSendOtp?: () => void;
  onVerifyOtp?: () => void;
  onBackToDetails?: () => void;
  resendCooldownSec?: number;
  otpNotice?: string | null;
  otpError?: string | null;
};

function moneyAbs(n: number) {
  return formatProposalMoneySafe(Math.abs(n));
}

function PricingSummary({ pricing }: { pricing: ProposalCustomerPricingDisplay }) {
  return (
    <div
      className="space-y-2.5 rounded-xl border border-[#1A2744]/10 bg-[#FAFAF7] p-4 text-sm"
      aria-label="Proposal amounts"
    >
      <div className="flex items-baseline justify-between gap-4">
        <span className="text-[#1A2744]/70">Proposal Total</span>
        <span className="font-medium tabular-nums text-[#1A2744]">
          {formatProposalMoneySafe(pricing.proposalTotal)}
        </span>
      </div>
      {pricing.showFinalAgreedPrice ? (
        <div className="flex items-baseline justify-between gap-4">
          <span className="text-[#1A2744]/70">Final Agreed Price</span>
          <span className="font-medium tabular-nums text-[#1A2744]">
            {formatProposalMoneySafe(pricing.finalAgreedPrice)}
          </span>
        </div>
      ) : null}
      {pricing.showGst ? (
        <div className="flex items-baseline justify-between gap-4">
          <span className="text-[#1A2744]/70">GST</span>
          <span className="font-medium tabular-nums text-[#1A2744]">
            {formatProposalMoneySafe(pricing.gstAmount)}
          </span>
        </div>
      ) : null}
      <div className="flex items-baseline justify-between gap-4 border-t border-[#1A2744]/10 pt-2.5">
        <span className="font-medium text-[#1A2744]">Amount payable</span>
        <span className="font-semibold tabular-nums text-[#1A2744]">
          {formatProposalMoneySafe(pricing.finalAmountPayable)}
        </span>
      </div>
    </div>
  );
}

export default function ProposalApprovalConfirmDialog({
  open,
  onOpenChange,
  variant = "proposal",
  proposalRef,
  optionLabel,
  customerPricing,
  hidePricingSummary = false,
  variationOrderSummary,
  approvalEmail,
  onApprovalEmailChange,
  onConfirm,
  confirming = false,
  confirmDisabled = false,
  otpStep = "details",
  approverName = "",
  onApproverNameChange,
  whatsappNumber = "",
  onWhatsappNumberChange,
  whatsappCountry = DEFAULT_APPROVAL_WHATSAPP_COUNTRY,
  onWhatsappCountryChange,
  otpCode = "",
  onOtpCodeChange,
  maskedWhatsApp = null,
  sendingOtp = false,
  verifyingOtp = false,
  onSendOtp,
  onVerifyOtp,
  onBackToDetails,
  resendCooldownSec = 0,
  otpNotice = null,
  otpError = null,
}: Props) {
  const trimmedEmail = approvalEmail.trim();
  const emailValid = !trimmedEmail || isReasonablyValidEmail(trimmedEmail);
  const isVo = variant === "variation_order";
  const title = isVo ? "Approve Variation Order" : "Approve Proposal";
  const nameOk = approverName.trim().length > 0;
  const phoneOk = whatsappNumber.trim().length > 0;
  const otpOk = /^\d{6}$/.test(otpCode.trim());
  const busy = sendingOtp || verifyingOtp || confirming;
  const countryOptions = useMemo(() => listApprovalWhatsAppCountries(), []);
  const selectedCountry =
    countryOptions.find((row) => row.code === whatsappCountry)?.code ?? DEFAULT_APPROVAL_WHATSAPP_COUNTRY;
  const selectedCallingCode =
    countryOptions.find((row) => row.code === selectedCountry)?.callingCode ?? "65";

  if (isVo) {
    const emailInputId = "vo-approval-email";
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md" data-testid="vo-approval-confirm-dialog">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>
              Enter your email address to confirm your approval. A copy of the approved Variation
              Order will be sent to this address.
              {variationOrderSummary?.voNumber ? (
                <>
                  {" "}
                  You are approving <strong>{variationOrderSummary.voNumber}</strong>
                  {proposalRef ? (
                    <>
                      {" "}
                      on proposal <strong>{proposalRef}</strong>
                    </>
                  ) : null}
                  .
                </>
              ) : null}
            </DialogDescription>
          </DialogHeader>
          {variationOrderSummary ? (
            <div className="space-y-3 rounded-lg border border-[#1A2744]/10 bg-[#FAFAF7]/80 p-4 text-sm">
              <div className="flex items-baseline justify-between gap-4">
                <span className="text-[#1A2744]/70">Variation Amount</span>
                <span className="font-medium tabular-nums text-[#1A2744]">
                  {moneyAbs(variationOrderSummary.variationAmount)}
                </span>
              </div>
              <div className="flex items-baseline justify-between gap-4">
                <span className="text-[#1A2744]/70">Revised Contract Value</span>
                <span className="font-medium tabular-nums text-[#1A2744]">
                  {moneyAbs(variationOrderSummary.revisedContractValue)}
                </span>
              </div>
              <div className="flex items-baseline justify-between gap-4">
                <span className="text-[#1A2744]/70">Amount Due Upon Confirmation</span>
                <span className="font-medium tabular-nums text-[#1A2744]">
                  {moneyAbs(variationOrderSummary.amountDueUponConfirmation)}
                </span>
              </div>
            </div>
          ) : null}
          <div className="space-y-2">
            <Label htmlFor={emailInputId}>Email address</Label>
            <Input
              id={emailInputId}
              data-testid="vo-approval-email-input"
              type="email"
              autoComplete="email"
              value={approvalEmail}
              onChange={(e) => onApprovalEmailChange(e.target.value)}
              placeholder="you@example.com"
            />
            {!trimmedEmail ? (
              <p className="text-sm text-[#1A2744]/60">
                Please enter a valid email address to continue.
              </p>
            ) : null}
            {trimmedEmail && !emailValid ? (
              <p className="text-sm text-red-600" data-testid="vo-approval-email-error" role="alert">
                Enter a valid email address.
              </p>
            ) : null}
          </div>
          <DialogFooter>
            <Button
              data-testid="vo-approval-confirm-button"
              disabled={confirmDisabled || confirming || !emailValid || !trimmedEmail}
              onClick={onConfirm}
            >
              {confirming ? "Approving…" : "Approve"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  const masked = maskedWhatsApp || maskWhatsAppDisplay(whatsappNumber);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (busy && !next) return;
        onOpenChange(next);
      }}
    >
      <DialogContent
        className="max-h-[90vh] overflow-y-auto sm:max-w-md"
        data-testid="proposal-approval-confirm-dialog"
        onOpenAutoFocus={(e) => {
          // Prefer focusing the first meaningful field for the current step.
          if (otpStep === "otp") {
            e.preventDefault();
            const el = document.getElementById("proposal-approval-otp");
            el?.focus();
          }
        }}
      >
        <DialogHeader className="space-y-3 text-left">
          <div className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.16em] text-[#C8A96A]">
            <span className={otpStep === "details" ? "text-[#1A2744]" : "text-[#1A2744]/45"}>
              1 · Your details
            </span>
            <span className="text-[#1A2744]/25" aria-hidden>
              —
            </span>
            <span className={otpStep === "otp" ? "text-[#1A2744]" : "text-[#1A2744]/45"}>
              2 · Verify
            </span>
          </div>
          <DialogTitle className="text-xl sm:text-2xl">{title}</DialogTitle>
          <DialogDescription className="text-sm leading-relaxed text-[#1A2744]/70 sm:text-[15px]">
            {otpStep === "details" ? (
              <>
                To complete your approval securely, we will send a one-time code to your WhatsApp.
                Your proposal is not approved until this code is verified.
                {optionLabel ? (
                  <>
                    {" "}
                    You are approving <strong className="text-[#1A2744]">{optionLabel}</strong>
                    {proposalRef ? (
                      <>
                        {" "}
                        on proposal <strong className="text-[#1A2744]">{proposalRef}</strong>
                      </>
                    ) : null}
                    .
                  </>
                ) : (
                  <>
                    {" "}
                    You are reviewing proposal{" "}
                    <strong className="text-[#1A2744]">{proposalRef}</strong>.
                  </>
                )}
              </>
            ) : (
              <>
                We have sent a 6-digit code to{" "}
                <strong className="text-[#1A2744]">{masked}</strong>. Enter it below to finish
                approving your proposal.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {customerPricing && !hidePricingSummary && otpStep === "details" ? (
          <PricingSummary pricing={customerPricing} />
        ) : null}

        {otpStep === "details" ? (
          <div className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="proposal-approval-name">Full name</Label>
              <Input
                id="proposal-approval-name"
                data-testid="proposal-approval-name-input"
                autoComplete="name"
                autoFocus
                value={approverName}
                onChange={(e) => onApproverNameChange?.(e.target.value)}
                placeholder="As you would like it recorded"
                aria-required="true"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="proposal-approval-whatsapp">WhatsApp number</Label>
              <div className="flex gap-2">
                <Select
                  value={selectedCountry}
                  onValueChange={(value) => onWhatsappCountryChange?.(value)}
                  disabled={sendingOtp || verifyingOtp}
                >
                  <SelectTrigger
                    className="h-10 w-[9.75rem] shrink-0 border-[#1A2744]/20"
                    data-testid="proposal-approval-whatsapp-country"
                    aria-label="WhatsApp country"
                  >
                    <SelectValue placeholder="Country" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {countryOptions.map((row) => (
                      <SelectItem key={row.code} value={row.code}>
                        {row.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  id="proposal-approval-whatsapp"
                  data-testid="proposal-approval-whatsapp-input"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel-national"
                  className="min-w-0 flex-1"
                  value={whatsappNumber}
                  onChange={(e) => onWhatsappNumberChange?.(e.target.value)}
                  placeholder={`e.g. +${selectedCallingCode} …`}
                  aria-required="true"
                  aria-describedby="proposal-approval-whatsapp-hint"
                />
              </div>
              <p id="proposal-approval-whatsapp-hint" className="text-xs leading-relaxed text-[#1A2744]/60">
                Select your country and enter the WhatsApp number where you would like to receive the
                verification code.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="proposal-approval-email">
                Email for confirmation{" "}
                <span className="font-normal text-[#1A2744]/55">(optional)</span>
              </Label>
              <Input
                id="proposal-approval-email"
                data-testid="proposal-approval-email-input"
                type="email"
                autoComplete="email"
                value={approvalEmail}
                onChange={(e) => onApprovalEmailChange(e.target.value)}
                placeholder="you@example.com"
                aria-describedby="proposal-approval-email-hint"
                aria-invalid={trimmedEmail && !emailValid ? true : undefined}
              />
              <p id="proposal-approval-email-hint" className="text-xs leading-relaxed text-[#1A2744]/60">
                Optional. If you add an email, we will send a PDF copy of the approved proposal for
                your records.
              </p>
              {trimmedEmail && !emailValid ? (
                <p
                  className="text-sm text-red-700"
                  data-testid="proposal-approval-email-error"
                  role="alert"
                >
                  That does not look like a valid email. Please check it, or leave the field blank.
                </p>
              ) : null}
            </div>
          </div>
        ) : (
          <div className="space-y-5">
            {customerPricing && !hidePricingSummary ? (
              <p className="text-sm text-[#1A2744]/70">
                Amount payable{" "}
                <span className="font-semibold text-[#1A2744]">
                  {formatProposalMoneySafe(customerPricing.finalAmountPayable)}
                </span>
              </p>
            ) : null}
            <div className="space-y-3">
              <Label htmlFor="proposal-approval-otp" className="sr-only">
                6-digit verification code
              </Label>
              <div className="flex justify-center sm:justify-start">
                <InputOTP
                  id="proposal-approval-otp"
                  data-testid="proposal-approval-otp-input"
                  maxLength={6}
                  value={otpCode}
                  onChange={(value) => onOtpCodeChange?.(value.replace(/\D/g, "").slice(0, 6))}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  containerClassName="gap-2 sm:gap-2.5"
                  aria-invalid={otpError ? true : undefined}
                  aria-describedby={otpError ? "proposal-approval-otp-error" : undefined}
                  disabled={verifyingOtp}
                >
                  <InputOTPGroup className="gap-2 sm:gap-2.5">
                    {Array.from({ length: 6 }).map((_, index) => (
                      <InputOTPSlot
                        key={index}
                        index={index}
                        className="h-12 w-10 rounded-lg border border-[#1A2744]/20 text-lg font-semibold text-[#1A2744] shadow-none first:rounded-lg first:border-l last:rounded-lg data-[active=true]:border-[#C8A96A] data-[active=true]:ring-[#C8A96A]/30 sm:h-14 sm:w-11 sm:text-xl"
                      />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
              </div>
              <p className="text-center text-xs text-[#1A2744]/55 sm:text-left">
                Codes expire shortly. You can paste a 6-digit code from WhatsApp.
              </p>
            </div>
            {otpError ? (
              <p
                id="proposal-approval-otp-error"
                className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800"
                role="alert"
              >
                {otpError}
              </p>
            ) : null}
            {otpNotice ? (
              <p className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-800" role="status">
                {otpNotice}
              </p>
            ) : null}
            <div className="space-y-3 rounded-xl border border-[#1A2744]/10 bg-[#FAFAF7] p-4">
              <div>
                <p className="text-sm font-semibold text-[#1A2744]">Didn’t receive the code?</p>
                <p className="mt-1 text-xs leading-relaxed text-[#1A2744]/60">
                  WhatsApp delivery can take up to a minute. Request a new code if it does not
                  arrive. Only the newest code will work.
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                className="h-10 w-full border-[#1A2744]/20 text-[#1A2744]"
                disabled={sendingOtp || resendCooldownSec > 0 || verifyingOtp}
                onClick={onSendOtp}
                aria-live="polite"
              >
                {resendCooldownSec > 0
                  ? `Resend code in ${resendCooldownSec}s`
                  : sendingOtp
                    ? "Sending a new code…"
                    : "Resend WhatsApp code"}
              </Button>
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
              <button
                type="button"
                className="text-[#1A2744]/70 underline decoration-[#1A2744]/20 underline-offset-4 transition hover:text-[#1A2744] hover:decoration-[#1A2744] disabled:opacity-50"
                disabled={verifyingOtp || sendingOtp}
                onClick={onBackToDetails}
              >
                Change details
              </button>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          {otpStep === "details" ? (
            <Button
              data-testid="proposal-approval-send-otp-button"
              className="h-11 w-full bg-[#1A2744] text-white hover:bg-[#1A2744]/90 sm:w-auto"
              disabled={confirmDisabled || sendingOtp || !nameOk || !phoneOk || !emailValid}
              onClick={onSendOtp}
            >
              {sendingOtp ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                  Sending code…
                </>
              ) : (
                "Send WhatsApp code"
              )}
            </Button>
          ) : (
            <Button
              data-testid="proposal-approval-confirm-button"
              className="h-11 w-full bg-[#1A2744] text-white hover:bg-[#1A2744]/90 sm:w-auto"
              disabled={confirmDisabled || verifyingOtp || !otpOk}
              onClick={onVerifyOtp}
            >
              {verifyingOtp ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                  Approving…
                </>
              ) : (
                "Confirm approval"
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
